import { Link } from "react-router-dom";
import { FaChess } from "react-icons/fa";
import { FiLayers, FiShield, FiShoppingBag } from "react-icons/fi";
import Section from "../components/layout/Section";
import Button from "../components/ui/Button";
import SocialLinks from "../components/ui/SocialLinks";
import TextLink from "../components/ui/TextLink";
import Reveal from "../components/ui/Reveal";
import AreaIcon from "../components/ui/AreaIcon";
import MediaCard from "../components/ui/MediaCard";
import ProjectRail from "../components/ui/ProjectRail";
import VideoStack from "../components/ui/VideoStack";
import {
  about,
  areas,
  chess,
  isListed,
  mobiltech,
  profile,
  listedProjects,
  technology,
} from "../data/site";

const areaIcons = {
  "/technology": FiShield,
  "/projects": FiLayers,
  "/chess": FaChess,
  "/business": FiShoppingBag,
};

export default function HomePage() {
  return (
    <>
      <header className="home-hero hero-in border-b border-[var(--line)] pb-14 pt-28 md:pb-16 md:pt-32">
        <div className="mx-auto w-full max-w-site px-5 sm:px-8">
          <h1 className="max-w-4xl text-5xl font-semibold leading-[1.05] tracking-tight text-[var(--ink)] sm:text-6xl">
            {profile.name}
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-[var(--muted)] md:text-xl">
            {profile.statement}
          </p>
          <ul className="mt-10 grid border-t border-[var(--line)] sm:grid-cols-2 lg:grid-cols-4 lg:border-y">
            {areas.map((area, index) => {
              const Icon = areaIcons[area.href];
              return (
                <li
                  key={area.href}
                  className={`hover-cell border-b border-[var(--line)] lg:border-b-0 lg:border-r ${
                    index === areas.length - 1 ? "lg:border-r-0" : ""
                  }`}
                >
                  <Link to={area.href} className="group block px-4 py-4 lg:px-5">
                    {Icon ? <Icon aria-hidden="true" size={18} className="text-[var(--accent)]" /> : null}
                    <span className="mt-3 block font-medium text-[var(--ink)] transition-colors group-hover:text-[var(--accent)]">
                      {area.label}
                    </span>
                    <span className="mt-1 block text-sm text-[var(--muted)]">{area.note}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
          <div className="mt-10">
            <SocialLinks />
          </div>
        </div>
      </header>

      <Reveal>
        <Section label="Technology" labelClass="home-label" title={technology.lede}>
          <dl className="max-w-xl divide-y divide-[var(--line)] border-y border-[var(--line)]">
            {technology.areas.slice(0, 3).map((area) => (
              <div key={area.title} className="grid gap-2 py-4 sm:grid-cols-5 sm:items-center sm:gap-4">
                <dt className="flex items-center gap-2 font-medium text-[var(--ink)] sm:col-span-2">
                  <AreaIcon title={area.title} size={16} />
                  {area.title}
                </dt>
                <dd className="text-[var(--muted)] sm:col-span-3">{area.text}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-2">
            <TextLink href="/technology">View technology</TextLink>
          </div>
          {isListed(technology.products) ? (
            <div className="mt-10">
              <h3 className="kicker">{technology.products.title}</h3>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {technology.products.items
                  .filter((item) => item.enabled !== false && item.home !== false)
                  .map((item) => (
                    <MediaCard
                      key={item.title}
                      href={item.href}
                      image={item.image}
                      kicker={item.title}
                      title={item.text}
                      action={item.action}
                      poster={item.poster || "tech"}
                    />
                  ))}
              </div>
              <VideoStack items={technology.products.videos} poster="tech" place="home" />
            </div>
          ) : null}
          {isListed(technology.videos) ? (
            <VideoStack
              items={[...technology.videos.items, ...technology.teaching.items]}
              poster="tech"
              place="home"
            />
          ) : null}
        </Section>
      </Reveal>

      <Reveal>
        <section className="border-t border-[var(--line)] py-14 md:py-16">
          <div className="mx-auto grid w-full min-w-0 max-w-site gap-6 px-5 sm:px-8 md:grid-cols-12 md:gap-8">
            <h2 className="kicker home-label md:col-span-3 md:pt-1">Projects</h2>
            <div className="min-w-0 md:col-span-9 md:col-start-4">
              <ProjectRail projects={listedProjects()} />
              <div className="mt-2">
                <TextLink href="/projects">All projects</TextLink>
              </div>
            </div>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section className="band-chess">
          <div className="mx-auto grid w-full max-w-site gap-6 px-5 py-14 sm:px-8 md:grid-cols-12 md:py-16">
            <h2 className="kicker home-label md:col-span-3 md:pt-2">Chess</h2>
            <div className="md:col-span-9 md:col-start-4">
              <div className="mb-5 h-0.5 w-16 bg-[var(--kicker)]" aria-hidden="true" />
              <p className="text-2xl font-semibold leading-snug tracking-tight">
                A personal interest in the game.
              </p>
              <p className="mt-5 max-w-xl leading-relaxed text-[var(--muted)]">
                Lichess, Chess.com, and a beginner course on Udemy.
              </p>
              <div className="mt-2">
                <TextLink href="/chess">Explore chess</TextLink>
              </div>
              <VideoStack
                items={[...chess.videos, ...chess.teaching.items]}
                poster="chess"
                place="home"
              />
            </div>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section className="band-business">
          <div className="mx-auto grid w-full max-w-site gap-6 px-5 py-12 sm:px-8 md:grid-cols-12 md:py-14">
            <h2 className="kicker home-label md:col-span-3 md:pt-2" style={{ color: "#ff9a62" }}>
              Business
            </h2>
            <div className="md:col-span-9 md:col-start-4">
              <p className="text-2xl font-semibold tracking-tight">{mobiltech.name}</p>
              <p className="mt-3 max-w-xl text-[#c5d0de]">{mobiltech.lede}</p>
              <div className="mt-5">
                <Button href={mobiltech.href} external variant="orange">
                  Visit MobilTech
                </Button>
              </div>
              <VideoStack items={mobiltech.videos} poster="business" place="home" />
            </div>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <Section label="About" labelClass="home-label" title={about.lead}>
          <p className="max-w-xl leading-relaxed text-[var(--text)]">{about.detail}</p>
          <div className="mt-2">
            <TextLink href="/about">About</TextLink>
          </div>
        </Section>
      </Reveal>

      <Reveal>
        <Section label="Contact" labelClass="home-label" title="A direct email, or the form.">
          <a
            className="text-[var(--ink)] underline decoration-[var(--line-strong)] underline-offset-4 transition hover:decoration-[var(--ink)]"
            href={`mailto:${profile.email}`}
          >
            {profile.email}
          </a>
          <div className="mt-2">
            <TextLink href="/contact">Contact</TextLink>
          </div>
        </Section>
      </Reveal>
    </>
  );
}
